"use strict";
import { getExploreDescription, getExploreTitle } from "pages/getExploreTitle";
import { getAddLiquidityPageTitle, getPositionPageDescription, getPositionPageTitle } from "pages/getPositionPageTitle";
import { Suspense, lazy, useMemo } from "react";
import { Navigate, matchPath, useLocation } from "react-router-dom";
import { isBrowserRouterEnabled } from "utils/env";
import i18n from "i18n";
const CreatePosition = lazy(() => import("pages/Pool/Positions/create/CreatePosition"));
const AddLiquidityV3WithTokenRedirects = lazy(() => import("pages/AddLiquidityV3/redirects"));
const AddLiquidityV2WithTokenRedirects = lazy(() => import("pages/AddLiquidityV2/redirects"));
const RedirectExplore = lazy(() => import("pages/Explore/redirects"));
const NotFound = lazy(() => import("pages/NotFound"));
const Home = lazy(() => import("pages/Home"));
const Portfolio = lazy(() => import("pages/Portfolio"));
const Vote = lazy(() => import("pages/Vote"));
const AiPage = lazy(() => import("features/agent/AiPage"));
const Liquidity = lazy(() => import("pages/Liquidity"));
const Locks = lazy(() => import("pages/Locks"));
const RelaysPage = lazy(() => import("pages/Relays"));
const ModifyLock = lazy(() => import("pages/Locks/ModifyLock"));
const CreateLock = lazy(() => import("pages/Locks/CreateLock"));
const DepositLock = lazy(() => import("pages/Locks/DepositLock"));
const BonusLock = lazy(() => import("pages/BonusLock"));
const LockRewardsPage = lazy(() => import("pages/LockRewards"));
const Incentivize = lazy(() => import("pages/Incentivize"));
const DepositLiquidity = lazy(() => import("pages/AddLiquidity"));
const Stake = lazy(() => import("pages/AddLiquidity/Stake"));
const Withdraw = lazy(() => import("pages/AddLiquidity/Withdraw"));
const AirdropClaim = lazy(() => import("pages/AirdropClaim"));
const LegacyPoolRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.LegacyPoolRedirects }))
);
const PoolFinderRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.PoolFinderRedirects }))
);
const LegacyPositionPageRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.LegacyPositionPageRedirects }))
);
const RemoveLiquidityV2WithTokenRedirects = lazy(
  () => import("pages/LegacyPool/redirects").then((module) => ({ default: module.RemoveLiquidityV2WithTokenRedirects }))
);
const PositionPage = lazy(() => import("pages/Pool/Positions/PositionPage"));
const V2PositionPage = lazy(() => import("pages/Pool/Positions/V2PositionPage"));
const PoolDetails = lazy(() => import("pages/PoolDetails"));
const TokenDetails = lazy(() => import("pages/TokenDetails"));
const Privacy = lazy(() => import("pages/Privacy"));
const Bridge = lazy(() => import("pages/Bridge"));
const Migrate = lazy(() => import("pages/Migrate"));
export function useRouterConfig() {
  const browserRouterEnabled = isBrowserRouterEnabled();
  const { hash } = useLocation();
  return useMemo(
    () => ({
      browserRouterEnabled,
      hash
    }),
    [browserRouterEnabled, hash]
  );
}
const StaticTitlesAndDescriptions = {
  TopazTitle: i18n.t("titlt.TopazTradeCrypto"),
  SwapTitle: i18n.t("title.buySellTradeEthereum"),
  SwapDescription: i18n.t("title.swappingMadeSimple"),
  DetailsPageBaseTitle: i18n.t("common.buyAndSell"),
  TDPDescription: i18n.t("title.realTime"),
  PDPDescription: i18n.t("title.tradeTokens"),
  AddLiquidityDescription: i18n.t("title.earnFees")
};
function createRouteDefinition(route) {
  return {
    getElement: () => null,
    getTitle: () => StaticTitlesAndDescriptions.TopazTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription,
    enabled: () => true,
    path: "/",
    nestedPaths: [],
    // overwrite the defaults
    ...route
  };
}
export const routes = [
  createRouteDefinition({
    path: "/",
    getTitle: () => StaticTitlesAndDescriptions.TopazTitle,
    getDescription: () => StaticTitlesAndDescriptions.SwapDescription,
    // `/` and `/swap` render the same UI; canonicalise to `/swap` so URL state
    // (input/output currency, etc.) always has a stable home.
    getElement: () => <Navigate to="/swap" replace />
  }),
  createRouteDefinition({
    path: "/explore",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    nestedPaths: [":tab", ":chainName", ":tab/:chainName"],
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/explore/tokens/:chainName/:tokenAddress",
    getTitle: () => i18n.t("common.buyAndSell"),
    getDescription: () => StaticTitlesAndDescriptions.TDPDescription,
    getElement: () => <TokenDetails />
  }),
  createRouteDefinition({
    path: "/tokens",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    getElement: () => <Navigate to="/explore/tokens" replace />
  }),
  createRouteDefinition({
    path: "/tokens/:chainName",
    getTitle: getExploreTitle,
    getDescription: getExploreDescription,
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/tokens/:chainName/:tokenAddress",
    getTitle: () => StaticTitlesAndDescriptions.DetailsPageBaseTitle,
    getDescription: () => StaticTitlesAndDescriptions.TDPDescription,
    getElement: () => <RedirectExplore />
  }),
  createRouteDefinition({
    path: "/explore/pools/:chainName/:poolAddress",
    getTitle: () => StaticTitlesAndDescriptions.DetailsPageBaseTitle,
    getDescription: () => StaticTitlesAndDescriptions.PDPDescription,
    getElement: () => <Suspense fallback={null}>
        <PoolDetails />
      </Suspense>
  }),
  createRouteDefinition({
    path: "/portfolio",
    getElement: () => <Portfolio />,
    getTitle: () => "Portfolio",
    getDescription: () => "Your Topaz account \u2014 balances, positions, veTOPAZ and rewards."
  }),
  createRouteDefinition({
    path: "/dashboard",
    getElement: () => <Navigate to="/portfolio" replace />
  }),
  createRouteDefinition({
    path: "/ai",
    getElement: () => <AiPage />,
    getTitle: () => "Topaz Prizm",
    getDescription: () => "Your on-chain DeFi strategist \u2014 chat with Topaz Prizm."
  }),
  createRouteDefinition({
    path: "/lockrewards/:lockId",
    getElement: () => <LockRewardsPage />,
    getTitle: () => "Lock Voting Rewards",
    getDescription: () => "View claimable voting rewards for a specific veTOPAZ lock."
  }),
  createRouteDefinition({
    path: "/stake",
    getElement: () => <Stake />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/withdraw",
    getElement: () => <Withdraw />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/vote",
    getElement: () => <Vote />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/privacy",
    getElement: () => <Privacy />,
    getTitle: () => "Private Swap",
    getDescription: () => "Private, non-custodial swaps on Topaz powered by SilentSwap"
  }),
  createRouteDefinition({
    path: "/bridge",
    getElement: () => <Bridge />,
    getTitle: () => "Bridge \u2014 Topaz",
    getDescription: () => "Move assets across chains with 0x cross-chain routing on Topaz"
  }),
  createRouteDefinition({
    path: "/migrate",
    getElement: () => <Migrate />,
    getTitle: () => "Migrate liquidity \u2014 Topaz",
    getDescription: () => "Move your PancakeSwap and Uniswap liquidity to Topaz in one transaction"
  }),
  createRouteDefinition({
    path: "/liquidity",
    getElement: () => <Liquidity />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/earn",
    getElement: () => <Navigate to="/liquidity" replace />
  }),
  createRouteDefinition({
    path: "/deposit",
    getElement: () => <DepositLiquidity />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/locks",
    getElement: () => <Locks />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/relays",
    getElement: () => <RelaysPage />,
    getTitle: () => "Relays",
    getDescription: () => "Deposit veTOPAZ into a relay and earn without managing a weekly vote."
  }),
  createRouteDefinition({
    path: "/lock",
    getElement: () => <CreateLock />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/bonuslock",
    getElement: () => <BonusLock />,
    getTitle: () => "Bonus Lock \u2014 Topaz",
    getDescription: () => "Lock TOPAZ and receive bonus veTOPAZ"
  }),
  createRouteDefinition({
    path: "/lock/deposit",
    getElement: () => <DepositLock />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/locks/modify-lock",
    getElement: () => <ModifyLock />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/incentives",
    getElement: () => <Incentivize />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/airdrop",
    getElement: () => <AirdropClaim />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/create-proposal",
    getTitle: () => i18n.t("title.createGovernanceOn"),
    getDescription: () => i18n.t("title.createGovernanceTo"),
    getElement: () => <Navigate to="/vote/create-proposal" replace />
  }),
  createRouteDefinition({
    path: "/swap",
    getElement: () => <Home />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/limit",
    getElement: () => <Home />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/dca",
    getElement: () => <Home />,
    getTitle: () => StaticTitlesAndDescriptions.SwapTitle
  }),
  createRouteDefinition({
    path: "/send",
    getElement: () => <Home />,
    getTitle: () => i18n.t("title.sendTokens")
  }),
  createRouteDefinition({
    path: "/positions/create",
    getElement: () => <Navigate to="/positions/create/v3" replace />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/create/:protocolVersion",
    getElement: () => <CreatePosition />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  // createRouteDefinition({
  //   path: '/positions',
  //   getElement: () => <Pool />,
  //   getTitle: getPositionPageTitle,
  //   getDescription: getPositionPageDescription,
  // }),
  createRouteDefinition({
    path: "/positions/v2/:chainName/:pairAddress",
    getElement: () => <V2PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v3/:chainName/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/positions/v4/:chainName/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  // Legacy pool routes
  createRouteDefinition({
    path: "/pool",
    getElement: () => <LegacyPoolRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/v2/find",
    getElement: () => <PoolFinderRedirects />,
    getTitle: getPositionPageDescription,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/v2",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pool/:tokenId",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pools/v2/find",
    getElement: () => <PoolFinderRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pools",
    getElement: () => <LegacyPoolRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/pools/:tokenId",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: getPositionPageTitle,
    getDescription: getPositionPageDescription
  }),
  createRouteDefinition({
    path: "/add/v2",
    nestedPaths: [":currencyIdA", ":currencyIdA/:currencyIdB"],
    getElement: () => <AddLiquidityV2WithTokenRedirects />,
    getTitle: getAddLiquidityPageTitle,
    getDescription: () => StaticTitlesAndDescriptions.AddLiquidityDescription
  }),
  createRouteDefinition({
    path: "/add",
    nestedPaths: [
      ":currencyIdA",
      ":currencyIdA/:currencyIdB",
      ":currencyIdA/:currencyIdB/:feeAmount",
      ":currencyIdA/:currencyIdB/:feeAmount/:tokenId"
    ],
    getElement: () => <AddLiquidityV3WithTokenRedirects />,
    getTitle: getAddLiquidityPageTitle,
    getDescription: () => StaticTitlesAndDescriptions.AddLiquidityDescription
  }),
  createRouteDefinition({
    path: "/remove/v2/:currencyIdA/:currencyIdB",
    getElement: () => <RemoveLiquidityV2WithTokenRedirects />,
    getTitle: () => i18n.t("title.removeLiquidityv2"),
    getDescription: () => i18n.t("title.removeTokensv2")
  }),
  createRouteDefinition({
    path: "/remove/:tokenId",
    getElement: () => <LegacyPositionPageRedirects />,
    getTitle: () => i18n.t("title.removePoolLiquidity"),
    getDescription: () => i18n.t("title.removev3Liquidity")
  }),
  createRouteDefinition({ path: "*", getElement: () => <Navigate to="/not-found" replace /> }),
  createRouteDefinition({ path: "/not-found", getElement: () => <NotFound /> })
];
export const findRouteByPath = (pathname) => {
  for (const route of routes) {
    const match = matchPath(route.path, pathname);
    if (match) {
      return route;
    }
    const subPaths = route.nestedPaths.map((nestedPath) => `${route.path}/${nestedPath}`);
    for (const subPath of subPaths) {
      const match2 = matchPath(subPath, pathname);
      if (match2) {
        return route;
      }
    }
  }
  return void 0;
};
